import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { AccountMovement } from 'common/state/accounts/actions';
import { accountMovementsRecipe } from 'common/state/accounts/recipes';
import { defaultAccountMovementsState } from 'common/state/accounts/states';
import { doGetAccountMovements } from 'common/state/accounts/thunkActions';
import { customerRecipe } from 'common/state/customers/recipes';
import { defaultCustomerState } from 'common/state/customers/states';
import { doGetCustomer } from 'common/state/customers/thunkActions';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

interface MovementParams {
  accountCode: string;
}

export const MovementsTable = () => {
  const { accountCode } = useParams<MovementParams>();
  const [customerState, customerDispatch] = useProducerWithThunks(
    customerRecipe,
    defaultCustomerState
  );
  const [movementsState, movementsDispatch] = useProducerWithThunks(
    accountMovementsRecipe,
    defaultAccountMovementsState
  );
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    movementsDispatch(
      doGetAccountMovements(accountCode, {
        limit: pageSize,
        offset: pageSize * (pageNo - 1),
      })
    );
  }, [movementsDispatch, pageSize, pageNo, accountCode]);

  const parsedAccountCode = useMemo(() => {
    const data: {
      customer?: string;
      contact?: string;
      account?: string;
      client?: string;
      reconciled?: string;
      pl?: null;
    } = {};

    const customerMatch = accountCode.match(/customer.[0-9]+/);
    const contactMatch = accountCode.match(/contact.[0-9]+/);
    const accountMatch = accountCode.match(/account.[a-z]+.[0-9]+/);
    const plMatch = accountCode.match(/.pl/);
    const clientMatch = accountCode.match(/client.[A-Z]+/);
    const reconciledMatch = accountCode.match(/reconciled.[A-Z]+/);

    if (customerMatch)
      data[customerMatch[0].split('.')[0]] = customerMatch[0].split('.')[1];
    if (contactMatch)
      data[contactMatch[0].split('.')[0]] = contactMatch[0].split('.')[1];
    if (clientMatch)
      data[clientMatch[0].split('.')[0]] = clientMatch[0].split('.')[1];
    if (reconciledMatch)
      data[reconciledMatch[0].split('.')[0]] = reconciledMatch[0].split('.')[1];
    if (accountMatch)
      data[
        `${accountMatch[0].split('.')[1]}-${accountMatch[0].split('.')[0]}`
      ] = accountMatch[0].split('.')[2];
    if (plMatch) data.pl = null;

    return data;
  }, [accountCode]);

  useEffect(() => {
    if (parsedAccountCode.customer)
      customerDispatch(doGetCustomer(parsedAccountCode.customer));
  }, [customerDispatch, parsedAccountCode.customer]);

  const columns = useMemo(
    (): ColumnProp<AccountMovement>[] => [
      {
        key: 'post_date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.post_date).toLocaleString(),
      },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({
            symbol: false,
          }),
      },
      {
        key: 'balance',
        title: 'Balance',
        valueFormatter: (row) =>
          new Currency(row.balance, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balance, row.currency).toString({
            symbol: false,
          }),
      },
      { key: 'description', title: 'Description' },
      { key: 'notes', title: 'Notes' },
    ],
    []
  );

  const title = useMemo(() => {
    let s = 'Movements: ';

    s += customerState.customer?.username
      ? `${customerState.customer.username} `
      : 'Loading... ';

    Object.entries(parsedAccountCode).forEach(([key, value]) => {
      if (!['contact', 'customer'].includes(key)) s += `${key} ${value || ''} `;
    });

    s += 'Account';

    return s;
  }, [customerState.customer, parsedAccountCode]);
  return (
    <>
      <BaseTable
        columns={columns}
        rows={movementsState.movements || []}
        csvName={title}
        isLoading={movementsState.pending}
        error={movementsState.error}
        pagination={{
          count: movementsState.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isSortable={false}
      />
    </>
  );
};
